// custom component (not registry-generated)
import type { VariantProps } from 'class-variance-authority'

import { cva } from 'class-variance-authority'

export { default as Badge } from './Badge.vue'
export { default as BadgeDot } from './BadgeDot.vue'

// RECONCILED to the accepted design intent for canonical Figma (intent sha256:4688a6d2…, decision
// DEC-0006, design review relayed 2026-09-17): one size, the approved tone set (no status
// vocabulary; status-coloured badges use the nearest approved hue), an optional leading dot,
// uppercase as the default presentation, and content-driven width. A canonical Figma set publishes
// the code's public contract and nothing else, so the contract mirrors that intent.
//
// `white` is painted from existing tokens (`card` on `background`), never a new token. `dot` is an
// axis rather than a story composition so the canonical set can publish it; `BadgeDot` stays
// exported for hand-composed cases. `uppercase` is an axis so the default presentation is governable.
//
// `rounded` keeps both approved shapes (design asked for the pill back, relayed 2026-09-18), with
// the toolkit tag corner as the default. The pill corner is the named `radius-pill` token rather
// than Tailwind's `rounded-full`: a corner radius is a token-bearing field and the registry is its
// only authority, so an unbound literal in Figma would read as drift on every variant that used it.
//
// `destructive` stays the red error token: Figma paints it coral today, identical to the `coral`
// tone, which the sync repaints rather than adopts (decision recorded 2026-09-17).
// The soft tints, one per tone. Shared with the Avatar fallback so a category letter and its chip
// are painted from the same record. The recipe below repeats them as literal compound variants on
// purpose: the governance engine reads a recipe only when its options are literal, so a spread
// would make Badge's contract unreadable. `Badge.spec.ts` holds the two in step.
export const softToneClasses = {
  neutral: 'bg-background text-foreground',
  lime: 'bg-secondary-subtle text-secondary-foreground',
  destructive: 'bg-destructive-subtle text-destructive',
  purple: 'bg-brand-purple-subtle text-foreground',
  orange: 'bg-brand-orange-subtle text-foreground',
  coral: 'bg-brand-coral-subtle text-foreground',
  pink: 'bg-brand-pink-subtle text-foreground',
  blue: 'bg-brand-blue-subtle text-foreground',
  yellow: 'bg-brand-yellow-subtle text-foreground',
  white: 'bg-card text-foreground',
} as const
export type SoftTone = keyof typeof softToneClasses

export const badgeVariants = cva(
  'inline-flex items-center justify-center border font-medium w-fit whitespace-nowrap shrink-0 gap-1 px-2 py-0.5 text-xs [&>svg]:size-3 [&>svg]:pointer-events-none focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background focus-visible:ring-2 aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive transition-[color,box-shadow] overflow-hidden [a&]:hover:opacity-90',
  {
    variants: {
      variant: {
        solid: 'border-transparent',
        soft: 'border-transparent',
        outline: 'bg-transparent',
      },
      tone: {
        neutral: '',
        lime: '',
        destructive: '',
        purple: '',
        orange: '',
        coral: '',
        pink: '',
        blue: '',
        yellow: '',
        white: '',
      },
      rounded: {
        true: 'rounded-pill',
        false: 'rounded-tag',
      },
      // The leading dot is a rendered child, so the axis only widens the gap the toolkit uses
      // between dot and label. `Badge.vue` renders the part itself.
      dot: {
        true: 'gap-1.5',
        false: '',
      },
      uppercase: {
        true: 'uppercase tracking-wide',
        false: '',
      },
    },
    compoundVariants: [
      // solid — filled
      { variant: 'solid', tone: 'neutral', class: 'bg-primary text-primary-foreground' },
      { variant: 'solid', tone: 'lime', class: 'bg-secondary text-secondary-foreground' },
      { variant: 'solid', tone: 'destructive', class: 'bg-destructive text-white' },
      { variant: 'solid', tone: 'purple', class: 'bg-brand-purple text-foreground' },
      { variant: 'solid', tone: 'orange', class: 'bg-brand-orange text-foreground' },
      { variant: 'solid', tone: 'coral', class: 'bg-brand-coral text-foreground' },
      { variant: 'solid', tone: 'pink', class: 'bg-brand-pink text-foreground' },
      { variant: 'solid', tone: 'blue', class: 'bg-brand-blue text-foreground' },
      { variant: 'solid', tone: 'yellow', class: 'bg-brand-yellow text-foreground' },
      // White is a surface, not a hue: it needs a visible edge to read as a tag, which is the
      // canvas token — exactly what the approved Figma variant strokes it with.
      { variant: 'solid', tone: 'white', class: 'bg-card text-foreground border-background' },
      // soft — tint. Neutral uses the canvas off-white (not bg-muted) per design;
      // on canvas-coloured surfaces pick another variant.
      { variant: 'soft', tone: 'neutral', class: 'bg-background text-foreground' },
      { variant: 'soft', tone: 'lime', class: 'bg-secondary-subtle text-secondary-foreground' },
      { variant: 'soft', tone: 'destructive', class: 'bg-destructive-subtle text-destructive' },
      { variant: 'soft', tone: 'purple', class: 'bg-brand-purple-subtle text-foreground' },
      { variant: 'soft', tone: 'orange', class: 'bg-brand-orange-subtle text-foreground' },
      { variant: 'soft', tone: 'coral', class: 'bg-brand-coral-subtle text-foreground' },
      { variant: 'soft', tone: 'pink', class: 'bg-brand-pink-subtle text-foreground' },
      { variant: 'soft', tone: 'blue', class: 'bg-brand-blue-subtle text-foreground' },
      { variant: 'soft', tone: 'yellow', class: 'bg-brand-yellow-subtle text-foreground' },
      // Derived, not reviewed: a tint of white is white. Design approved white in `solid`, and an
      // axis product has to answer for the other two appearances.
      { variant: 'soft', tone: 'white', class: 'bg-card text-foreground' },
      // outline — the accent carries the colour on the border; the label stays ink, because
      // every accent is under 2.5:1 as text on the canvas.
      { variant: 'outline', tone: 'neutral', class: 'border-border text-foreground' },
      { variant: 'outline', tone: 'lime', class: 'border-secondary text-foreground' },
      { variant: 'outline', tone: 'destructive', class: 'border-destructive text-destructive' },
      { variant: 'outline', tone: 'purple', class: 'border-brand-purple text-foreground' },
      { variant: 'outline', tone: 'orange', class: 'border-brand-orange text-foreground' },
      { variant: 'outline', tone: 'coral', class: 'border-brand-coral text-foreground' },
      { variant: 'outline', tone: 'pink', class: 'border-brand-pink text-foreground' },
      { variant: 'outline', tone: 'blue', class: 'border-brand-blue text-foreground' },
      { variant: 'outline', tone: 'yellow', class: 'border-brand-yellow text-foreground' },
      { variant: 'outline', tone: 'white', class: 'border-background text-foreground' },
    ],
    defaultVariants: {
      variant: 'solid',
      tone: 'neutral',
      rounded: false,
      dot: false,
      // Uppercase is the presentation design asked for by default; a call site opts out.
      uppercase: true,
    },
  },
)
export type BadgeVariants = VariantProps<typeof badgeVariants>

// The toolkit's leading dot, as a family part rather than 17 hand-rolled spans.
// `current` (the default) inherits the chip's own text colour, so the dot reads as
// ink on a lime/soft chip and follows the tone everywhere else without a
// cross-product of tone x dot compound variants. An explicit tone covers the
// DotTag case, where a neutral/outline chip delegates the category colour to the
// dot instead of the fill. Size and `aria-hidden` live here so no call site can
// drift from the brand.
export const badgeDotVariants = cva(
  'size-2 shrink-0 rounded-full',
  {
    variants: {
      tone: {
        current: 'bg-current',
        neutral: 'bg-primary',
        lime: 'bg-secondary',
        destructive: 'bg-destructive',
        purple: 'bg-brand-purple',
        orange: 'bg-brand-orange',
        coral: 'bg-brand-coral',
        pink: 'bg-brand-pink',
        blue: 'bg-brand-blue',
        yellow: 'bg-brand-yellow',
        white: 'bg-card',
      },
    },
    defaultVariants: {
      tone: 'current',
    },
  },
)
export type BadgeDotVariants = VariantProps<typeof badgeDotVariants>
